import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import useNodeMetadata from '../models/useNodeMetadata'
import type { NodeCompute } from '../utilities/computeNode'
import { NODE_MARK_TITLES, nodeMarks, type NodeMark } from '../utilities/nodeMarks'
import {
  AWS_PATH,
  AWS_VIEWBOX,
  CLOUD_PATH,
  CLOUD_VIEWBOX,
  GROK_BOT_URL,
  NOBGP_ORANGE,
  TAILSCALE_PATH,
  TAILSCALE_VIEWBOX,
} from '../utilities/nodeMarkIcons'

type Props = {
  nodeId: string | undefined
  /** The node's open compute task (`useComputeNodes`): noBGP provisioned it. Its label (class and
   * when it stops) is the noBGP cloud's tooltip, as the compute chip's was. */
  compute?: NodeCompute
}

const SIZE = 14

function MarkIcon({ mark }: { mark: NodeMark }) {
  switch (mark) {
    case 'grok':
      return <img src={GROK_BOT_URL} width={SIZE} height={SIZE} alt="" />
    case 'nobgp':
      return (
        <svg width={SIZE} height={SIZE} viewBox={CLOUD_VIEWBOX} aria-hidden>
          <path d={CLOUD_PATH} fill={NOBGP_ORANGE} />
        </svg>
      )
    case 'aws':
      return (
        <svg width={SIZE + 4} height={SIZE} viewBox={AWS_VIEWBOX} aria-hidden>
          <path d={AWS_PATH} fill="currentColor" />
        </svg>
      )
    case 'tailscale':
      return (
        <svg width={SIZE - 2} height={SIZE - 2} viewBox={TAILSCALE_VIEWBOX} aria-hidden>
          <path d={TAILSCALE_PATH} fill="currentColor" />
        </svg>
      )
  }
}

/**
 * The marks after a node's name in the node list (nobgp-router#2010): Grok Bot, noBGP cloud or
 * AWS, then Tailscale when it runs. The rule is `utilities/nodeMarks.ts`; the map draws the same
 * marks (`graph/renderer.ts`).
 */
export default function NodeMarks({ nodeId, compute }: Props) {
  const metadata = useNodeMetadata(state => (nodeId ? state.getByNodeId(nodeId) : undefined))
  const marks = nodeMarks({
    bot: metadata?.bot,
    tailscale: metadata?.tailscale,
    cloud: metadata?.cloud,
    provisioned: !!compute,
  })
  return (
    <>
      {marks.map(mark => (
        <Tooltip key={mark} title={mark === 'nobgp' && compute ? compute.label : NODE_MARK_TITLES[mark]}>
          <Box
            component="span"
            role="img"
            aria-label={NODE_MARK_TITLES[mark]}
            sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.secondary' }}
          >
            <MarkIcon mark={mark} />
          </Box>
        </Tooltip>
      ))}
    </>
  )
}
